import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import groupBy from 'lodash-es/groupBy';
import sortBy from 'lodash-es/sortBy';
import React from 'react';
import styled from 'styled-components';
import {
  CountryProfile,
  CountryYear,
  Location,
  PolicyRecommendation,
  ProductClass,
  ProductLevel,
  TreeMapInput,
  TreeMapProduct,
  TreeMapType,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { failIfValidOrNonExhaustive } from '../../Utils';
import { getCountryName } from '../../Utils';
import {
  getWeightsForPolicyCombination,
  numTopProductsToBeHighlighted,
} from '../feasibilityChart/transform';
import Localized from '../landing/Localized';
import {
  policyColors,
} from '../rockSongChart/Utils';
import { PolicyDimension } from '../Utils';
import {
  IconContainer,
  iconMargin,
  iconWidth,
  PolicyName,
  PolicyNameContainer as PolicyNameContainerBase,
} from './DiversificationPaths';
import {
  ContentContainer,
  HighlightedText,
  IntroText,
  Title,
} from './SharedStyling';
import Slide from './Slide';
import SlideAdvancer, { Direction } from './SlideAdvancer';

const PolicyNameContainer = styled(PolicyNameContainerBase)`
  flex-wrap: wrap;
  text-align: left;
`;

const PolicyDimensionText = styled.div`
  padding-left: ${iconWidth + iconMargin}px;
  flex-basis: 100%;
`;

const query = gql`
  query HighPotentialProductsSideBar(
    $facet: TreeMapType!,
    $productClass: ProductClass!,
    $productLevel: ProductLevel!,
    $locationLevel: LocationLevel,
    $product: ID,
    $partner: ID,
    $location: ID!,
    $year: Int!,
  ) {
    location(id: $location) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    countryProfile(location: $location) {
      policyRecommendation
      latestECIRank: latestEciRank
    }
    countryYear(location: $location, year: $year) {
      eci
    }
    treeMap(
      facet: $facet,
      productClass: $productClass,
      year: $year,
      productLevel: $productLevel,
      locationLevel: $locationLevel,
      location: $location,
      product: $product,
      partner: $partner,
      mergePci: true,
    ) {
      ...on TreeMapProduct {
        product {
          id
          neverShow: hideFeasibility
          parent {
            id
            shortName
          }
          topLevelParent {
            id
          }
        }
        pci
        normalizedPCI: normalizedPci
        normalizedDistance
        normalizedOpportunityGain
      }
    }
  }
`;

interface FetchDatum {
  product: {
    id: TreeMapProduct['product']['id'];
    neverShow: TreeMapProduct['product']['hideFeasibility'];
    parent: null | {
      id: TreeMapProduct['product']['id'];
      shortName: TreeMapProduct['product']['shortName'];
    }
    topLevelParent: {
      id: TreeMapProduct['product']['topLevelParent']['id'];
    }
  };
  pci: TreeMapProduct['pci'];
  normalizedPCI: TreeMapProduct['normalizedPci'];
  normalizedDistance: TreeMapProduct['normalizedDistance'];
  normalizedOpportunityGain: TreeMapProduct['normalizedOpportunityGain'];
}

interface SuccessResponse {
  location: null | {
    id: Location['id'],
    shortName: Location['shortName'],
    nameAbbr: Location['nameAbbr'],
    thePrefix: Location['thePrefix'],
  };
  countryProfile: null | {
    policyRecommendation: CountryProfile['policyRecommendation'],
    latestECIRank: CountryProfile['latestEciRank'],
  };
  countryYear: null | {
    eci: CountryYear['eci'],
  };
  treeMap: FetchDatum[];
}

const getTopProductSectors = (
  allProducts: FetchDatum[],
  policyRecommendation: PolicyRecommendation,
  policyDimension: PolicyDimension,
  countryECI: number): [string, string] => {

  // get the weighted distance, complexity, and opportunity gain multipliers
  const { distance, complexity, opportunityGain }
    = getWeightsForPolicyCombination(policyRecommendation, policyDimension);

  // clean the allProducts array by removing any that have null values,
  // remove "never show" products and remove products less complex than
  // country's ECI:
  const cleanedAllProducts = allProducts.filter(
    ({ normalizedPCI, normalizedDistance, normalizedOpportunityGain, product, pci }) =>
    (normalizedPCI !== null
      && normalizedDistance !== null
      && normalizedOpportunityGain !== null
      && product.neverShow === false
      && pci !== null
      && pci > countryECI
      && product.parent !== null
      && product.topLevelParent.id !== 'product-HS-10'), // filter out "Other"
    );


  // sort the products and return only the top 'n' products
  const sortedProducts = sortBy( cleanedAllProducts,
    [ ({ normalizedPCI, normalizedDistance, normalizedOpportunityGain }) => {
    if (normalizedPCI !== null && normalizedDistance !== null && normalizedOpportunityGain !== null) {
      return normalizedPCI * complexity +  normalizedDistance * distance +  normalizedOpportunityGain * opportunityGain;
    }
  }]).reverse();
  const topProducts = sortedProducts.slice(0, numTopProductsToBeHighlighted);

  // group each sector based on number of their products appearing within the top 'n' products
  const sectorGroups = groupBy(topProducts, ({product}) => {
    if (product.parent !== null) {
      return product.parent.id;
    }
  });
  
  // sort sectors based on product count. sorting is done from lowest to highest
  const sortedSectorGroups = sortBy(sectorGroups, sector => sector.length);

  // get the top two sectors and return them
  const sector1 = sortedSectorGroups[sortedSectorGroups.length - 1][0].product.parent;
  const sector2 = sortedSectorGroups[sortedSectorGroups.length - 2][0].product.parent;
  const sector1Name = sector1 !== null ? sector1.shortName : 'N/A';
  const sector2Name = sector2 !== null ? sector2.shortName : 'N/A';
  return [sector1Name, sector2Name];
};

interface Props {
  onNextSlideClick: () => void;
  onPrevSlideClick: () => void;
  getFluentString: GetString;
  fluentBundle: FluentBundle;
  countryId: string;
  policyDimension: PolicyDimension;
  year: number;
  isHelpActive: boolean;
  helpTooltipIndex: number;
}

const HighPotentialProducts = (props: Props) => {
  const {
    onNextSlideClick, onPrevSlideClick, getFluentString,
    countryId, policyDimension, fluentBundle,
    year, isHelpActive, helpTooltipIndex,
  } = props;

  const variables: TreeMapInput = {
    facet: TreeMapType.CPY_C,
    productClass: ProductClass.HS,
    year,
    productLevel: ProductLevel.fourDigit,
    locationLevel: null,
    location: countryId,
    product: null,
    partner: null,
  };

  const {loading, error, data} = useQuery<SuccessResponse, TreeMapInput>(query, {variables});

  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = null;
  } else if (error !== undefined) {
    console.error(error);
    out = null;
  } else if (data !== undefined) {

    const {location, treeMap, countryProfile, countryYear} = data;
    if (location === null || countryProfile === null || countryYear === null) {
      out = null;
    } else {
      const {shortName, nameAbbr, thePrefix} = location;
      const {policyRecommendation: policy, latestECIRank} = countryProfile;
      const {eci} = countryYear;
      const {namePrimary, nameSecondary} = getCountryName({shortName, nameAbbr, thePrefix: Boolean(thePrefix)});
      if (policy === null || eci === null) {
        out = null;
      } else {
        let slideContent: React.ReactElement<any> | null;
        if (policy === PolicyRecommendation.TechFrontier) {
          slideContent = (
            <IntroText>
              {getFluentString('early-exit-sidebar-text', {
                'country-name-first': namePrimary,
                'country-name': nameSecondary,
              })}
            </IntroText>
          );
        } else {
          const [sectorToDiversifyOne, sectorToDiversifyTwo] =
            getTopProductSectors(treeMap, policy, policyDimension, eci);
          const IntroTextArgs = {
            'country-name-first': namePrimary,
            'country-name': nameSecondary,
            'sectorOne': (
              <HighlightedText
                children={sectorToDiversifyOne}
              />
            ),
            'sectorTwo': (
              <HighlightedText
                children={sectorToDiversifyTwo}
              />
            ),
          };
          const policyColor = policyColors[policy];
          const policyNameStyle: React.CSSProperties = {
            color: policyColor,
          };
          const iconStyle: React.CSSProperties = {
            '--icon-color': policyColor,
          };
          let policyName: string;
          if (policy === PolicyRecommendation.ParsimoniousIndustrial) {
            policyName = getFluentString('strategic-approach-name-parsimonious-industrial');
          } else if (policy === PolicyRecommendation.StrategicBets) {
            policyName = getFluentString('strategic-approach-name-strategic-bets');
          } else if (policy === PolicyRecommendation.LightTouch) {
            policyName = getFluentString('strategic-approach-name-light-touch');
          } else {
            failIfValidOrNonExhaustive(policy, 'Invalid policy ' + policy);
            policyName = '';
          }
          let policyDimensionName: string;
          if (policyDimension === PolicyDimension.LowHangingFruit) {
            policyDimensionName = getFluentString('policy-dimensions-control-radio-label-low-hanging-fruit');
          } else if (policyDimension === PolicyDimension.BalancedPortfolio) {
            policyDimensionName = getFluentString('policy-dimensions-control-radio-label-balanced-portfolio');
          } else if (policyDimension === PolicyDimension.LongJump) {
            policyDimensionName = getFluentString('policy-dimensions-control-radio-label-long-jumps');
          } else {
            policyDimensionName = '';
          }
          slideContent = (
            <>
              <IntroText>
                <PolicyNameContainer>
                  <IconContainer
                    style={iconStyle}
                    dangerouslySetInnerHTML={{__html: require('../rockSongChart/policy.svg')}}
                  />
                  <PolicyName style={policyNameStyle}>{policyName}</PolicyName>
                  <PolicyDimensionText style={policyNameStyle}>{policyDimensionName}</PolicyDimensionText>
                </PolicyNameContainer>
                <Localized
                  bundle={fluentBundle}
                  messageId='high-potential-products-text-para-1'
                  messageArgs={IntroTextArgs}
                >
                  <div />
                </Localized>
              </IntroText>
            </>
          );
        }

        const slideAdvancerText = getFluentString('section-nav-slide-growth-expectations', {
          'country-name': shortName,
        });
        out = (
          <>
            <SlideAdvancer
            onClick={onPrevSlideClick}
            direction={Direction.Previous}
            isHelpActive={isHelpActive}
            helpTooltipIndex={helpTooltipIndex}
            getFluentString={getFluentString}
          >
              {getFluentString('section-nav-slide-growth-opportunities')}
            </SlideAdvancer>
            <ContentContainer>
              <Title>
                {getFluentString('section-nav-slide-high-potential-products')}
              </Title>
              {slideContent}
            </ContentContainer>
            <SlideAdvancer
            onClick={onNextSlideClick}
            direction={Direction.Next}
            isHelpActive={isHelpActive}
            helpTooltipIndex={helpTooltipIndex}
            getFluentString={getFluentString}
          >
              {slideAdvancerText}
            </SlideAdvancer>
          </>
        );
      }
    }
  } else {
    out = null;
  }

  return (
    <Slide>
      <>
        {out}
      </>
    </Slide>
  );
};

export default HighPotentialProducts;
